import * as React from 'react';
import { Query } from 'react-apollo';
import { ApolloQueryResult } from 'apollo-client';

import EventTracking from 'lib/EventTracking';
import ToastActions from 'actions/ToastActions';
import ErrorReporter from 'app/components/ErrorReporter';
import Notice from 'components/Notice';
import Text from 'components/Text';

import GroupListTable from 'groups/components/GroupList/GroupListTable';
import GroupsListFetcher from 'groups/containers/GroupList/GroupsListFetcher';

// @ts-ignore
import { SortDirection } from 'components/ComposableTable';
// @ts-ignore
import { Sort } from 'clients/api/organizationClasses';
import { GroupRow, GroupStatus } from 'groups/components/GroupList/GroupListTable';
import { ActiveGroupsQuery } from './queries/__generated__/ActiveGroupsQuery';
import { UnclaimedGroupsQuery } from './queries/__generated__/UnclaimedGroupsQuery';

type Props = {
  onGroupClicked: (row: GroupRow) => void;
  openGroupKey: string | null;
  orgUuid: string;
  groupStatus: GroupStatus;
  query: string | null;
  reloadQuery: boolean;
  sortType: Sort;
  sortDirection: SortDirection;
  onSortChange: (sortType: Sort, sortDirection: SortDirection) => void;
};

class GroupListTableContainer extends React.Component<Props> {
  _refetch:
    | (() => Promise<ApolloQueryResult<ActiveGroupsQuery | UnclaimedGroupsQuery>>)
    | null = null;

  componentDidUpdate(prevProps: Props) {
    const { reloadQuery } = this.props;
    const refetch = this._refetch;

    if (reloadQuery && !prevProps.reloadQuery && refetch) {
      refetch();
    }
  }

  handleSort = (sortType: Sort, sortDirection: SortDirection) => {
    const { onSortChange } = this.props;

    if (sortType === 'name') {
      EventTracking.trackKey('admin_dashboard.class_list.table.sort_class_name.click');
    } else if (sortType === 'created') {
      EventTracking.trackKey('admin_dashboard.class_list.table.sort_date_created.click');
    }

    onSortChange(sortType, sortDirection);
  };

  renderError = () => {
    ToastActions.showDefaultErrorToast();

    return (
      <Text align="center">
        <ErrorReporter error="GroupListTableContainer - couldn't load data" />
        <Notice color="white">
          Sorry—no data is available at this time. Try refreshing the page.
        </Notice>
      </Text>
    );
  };

  render() {
    const {
      onGroupClicked,
      openGroupKey,
      orgUuid,
      groupStatus,
      query,
      sortType,
      sortDirection,
    } = this.props;

    return (
      <GroupsListFetcher
        groupStatus={groupStatus}
        orgUuid={orgUuid}
        query={query}
        sortDirection={sortDirection}
        sortType={sortType}
        handleError={this.renderError}>
        {({ handleFetchMore, loading, refetch, groups, hasNextPage }) => {
          if (refetch) {
            this._refetch = refetch;
          }

          if (!groups || !handleFetchMore) {
            return null;
          }

          return (
            <GroupListTable
              fetchMore={handleFetchMore}
              hasNextPage={hasNextPage}
              groupStatus={groupStatus}
              groups={groups}
              loading={loading}
              query={query}
              onGroupClicked={onGroupClicked}
              onSort={this.handleSort}
              openGroupKey={openGroupKey}
            />
          );
        }}
      </GroupsListFetcher>
    );
  }
}

export default GroupListTableContainer;
